import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getSearchSdkLocale } from 'i18n'
import StoreFunctions from 'stores/storeFunctions'
import SimpleStore from 'stores/simpleStore'
import { ProductCollectionPage } from '@unbridaled/search-sdk'
import { Preloader } from 'lib/components/preloader'
import { ErrorModal } from 'lib/components/modals/ErrorModal'
import { ConfirmationModal } from 'lib/components/modals/ConfirmationModal'
import { DASOrderSuccessfullModal } from 'lib/components/modals/DASOrderSuccessfullModal'
import { JourneyBar } from 'lib/components/journeyBar'
import { JewelryType } from 'constants/jewelryTypes'
import { useWishlist } from 'utils/use-wishlist'
import { truncateCurrencyAmount } from 'constants/currencies'
import { Roles } from 'constants/roles'
import logger from 'utils/logger'
import { BespokePageState, RatioTemplate, DiamondWeight, ProductSettingResponse, DefaultCollectionResponse, FindDiamondWeightsResponse, ProductCollectionPageProps } from 'types/interfaces'
import { useAppContext } from 'lib/context/AppContext'

export interface BespokeComponentState {
  ratios: RatioTemplate[]
  collection?: DefaultCollectionResponse | null
  product?: ProductSettingResponse | null
  weights: FindDiamondWeightsResponse['weights'] | null
  size?: DiamondWeight | null
}

export const BespokePage: React.FC<any> = props => {
  const { t } = useTranslation()
  const { diamondStore, appConfig, enabledSourceTypes, currencyInfo, typeLabels } = useAppContext()

  const history = useHistory()
  const storeFunctions = new StoreFunctions()
  const simpleStore = useMemo(() => new SimpleStore(), [])
  const [role] = useState(storeFunctions.getUserRole())
  const [loaded, setLoaded] = useState(false)
  const [isMemoMode] = useState(storeFunctions.isMemoMode())
  // Shop base (merchant) currency, so bespoke prices the SAME way as the other
  // surfaces: retail_price in merchant currency x the Shopify presentment rate.
  const [shopCurrency, setShopCurrency] = useState<string | null>(null)
  const config = useMemo(() => ({
    sourceTypes: appConfig?.source_types,
    advancedSearchCartEnabled: appConfig?.advanced_search_cart_enabled,
    ringSizeRegion: appConfig?.ring_size_region
  }), [appConfig])
  
  const [state, setState] = useState<BespokeComponentState>({
    ratios: [],
    weights: null,
    collection: null,
    product: null,
    size: null
  })
  const [shopName, setShopName] = useState(null)
  const bespokeType = props.bespokeType
  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const [error, setError] = useState<BespokePageState['error'] | null>(null)
  const [selectedGems, setSelectedGems] = useState<Array<any>>([])
  const [selectedGemsOrderType, setSelectedGemsOrderType] = useState('i')
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [dasOrderSuccessfullModalOpen, setDasOrderSuccessfullModalOpen] = useState(false)
  const [showJourneyBar, setShowJourneyBar] = useState(false)

  const fetchShopName = async (): Promise<void | null> => {
    try {
      const shopInfo = await diamondStore.getShopName()
      setShopName(shopInfo.shopName)
    } catch (e) {
      logger.error('Failed to get shop name')
      return null
    }
  }

  const fetchShopCurrency = async (): Promise<void | null> => {
    try {
      const currencyData = await simpleStore.ajax('GET', 'shop_currency')
      if (currencyData?.currency) {
        setShopCurrency(currencyData.currency)
        return
      }
      throw new Error('Failed to get shop currency')
    } catch (e) {
      logger.error('Failed to get shop currency')
      return null
    }
  }

  const normalizeRetailPrice = useCallback((value: unknown): number => {
    const parsed = typeof value === 'string' ? Number(value.replace(/[\,]/g, '')) : Number(value)
    if (!Number.isFinite(parsed)) return 0
    return Math.round(parsed * 100) / 100
  }, [])

  const fetchCollections = async (): Promise<void | null> => {
    try {
      const collections = await diamondStore.getDefaultCollections()
      if (collections) {
        setState((prevState) => ({
          ...prevState,
          collection: collections.find((collection: { [key: string]: any }) => collection.id === bespokeType)
        }));
      }
    } catch (e) {
      logger.info(e)
      return null
    }
  }

  const fetchRatios = async (): Promise<void | null> => {
    try {
      const ratios = await diamondStore.getRatios()
      if (ratios) {
        setState((prevState) => ({
          ...prevState,
          ratios: ratios.ratio_templates
        }));
      }
    } catch (e) {
      logger.info(e)
      return null
    }
  }

  const getDiamondsByMerchantTemplates = useCallback(
    async (params: { [key: string]: any }): Promise<{ [key: string]: any }[] | null> => {
      try {
        const isRegularMode = role !== Roles.Merchant && !isMemoMode
        // Always request DAS in the shop's merchant (base) currency so retail /
        // wholesale come back in the merchant currency (e.g. AUD) the Shopify
        // variant is minted from — not USD.
        params.currency_code = shopCurrency || currencyInfo.currency
        const gems = await diamondStore.getDiamondsByMerchantTemplates(params, role)
        if (!Array.isArray(gems)) {
          return gems
        }
        // Merchant/memo (double) mode: keep prices in the shop currency; the SDK
        // converts the customer-currency line with currencyProps.currencyRate
        // (the Shopify rate) below — same as the other surfaces.
        if (!isRegularMode) {
          return gems
        }
        // Regular (single) mode: convert to the customer currency here by the
        // Shopify presentment rate (not DAS's rate).
        const shopifyRate =
          shopCurrency && currencyInfo.currency !== shopCurrency
            ? window.Shopify?.currency?.rate || 1
            : 1
        return gems.map((gem) => {
          const storeRetailPrice = normalizeRetailPrice(gem?.retail_price)
          const converted = truncateCurrencyAmount(storeRetailPrice * shopifyRate)
          console.log(
            `[price][bespoke] ${shopCurrency || 'base'} ${storeRetailPrice} -> ${currencyInfo.currency} ${converted} @ Shopify rate ${shopifyRate}`
          )
          return { ...gem, retail_price: converted, currency_code: currencyInfo.currency }
        })
      } catch (e) {
        logger.info(e)
        return null
      }
    },
    [role, isMemoMode, shopCurrency, currencyInfo.currency, normalizeRetailPrice]
  )

  const getDiamondWeights = async (): Promise<void> => {
    try {
      const params = {
        weightMin: state.product?.weight_min,
        weightMax: state.product?.weight_max
      }
      const rawWeights = await diamondStore.getDiamondWeights(params)
      const weights = Array.isArray(rawWeights) ? rawWeights : null
      
      setState((prevState) => ({
        ...prevState,
        weights
      }));
      const uri = window.location.href
      if (uri.includes('#')) {
        const [, , rawSize = ''] = uri.split('#')
        const size = decodeURI(rawSize)
        setState((prevState) => ({
          ...prevState,
          size: weights?.find((value: DiamondWeight) => value.weight === size) || null
        }));
      }
    } catch (e) {
      logger.info(e)
    }
  }

  const getRatio = (ratios: RatioTemplate[], product: ProductSettingResponse): RatioTemplate | null | undefined => {
    if (!ratios) return null
    return ratios.find(
      template => template.shape === product.shape && template.ratio_group === product.ratio_group
    )
  }

  useEffect(() => {
    fetchShopName()
    fetchShopCurrency()
    fetchCollections()
    fetchRatios()
    setLoaded(true)
  }, [])

  useEffect(() => {
    const listener = (uri: string): void => {
      if (uri.includes('#') && state.collection) {
        const [, rawShape = '', rawSize = ''] = uri.split('#')
        const shape = decodeURI(rawShape)
        const size = decodeURI(rawSize)
        let product = shape
          ? state.collection?.product_settings.find(
              (product: { [key: string]: any }) => product.title === shape
            ) || null
          : null
        if (product) {
          const ratio = getRatio(state.ratios, product)
            product = {
            ...product,
            ...(ratio ?? {}),
            ratio_min: ratio ? ratio.ratio_min : product.ratio_min,
            ratio_max: ratio ? ratio.ratio_max : product.ratio_max
          }
        }
        setState((prevState) => ({
          ...prevState,
          size: Array.isArray(prevState.weights)
            ? prevState.weights.find((value) => value.weight === size) || null
            : null,
          product: product
        }));
        setShowJourneyBar(true)
      } else {
        setState((prevState) => ({
          ...prevState,
          product: null
        }));
      }
    }
    listener(window.location.href)
  }, [state.collection, state.ratios, window.location.hash])

  useEffect(() => {
    if (state.product) {
      getDiamondWeights()
    }
  }, [state.product])

  const onProductCollectionChange = (product: { [key: string]: any }): void => {
    const shapeSelected = location.hash != "";
    if (!shapeSelected){
      document.querySelector("body")?.scrollIntoView({ block: "start" });
    }
    const urlSearchParams = new URLSearchParams(window.location.search)
    const jType = urlSearchParams.get('jewelry_type')
    const jewelryType = jType !== null ? `?jewelry_type=${jType}` : ''
    history.push(`${jewelryType}#${product.title}${state.size ? `#${state.size.weight}` : ''}`)
  }

  const onSizeChange = (size: DiamondWeight): void => {
    const urlSearchParams = new URLSearchParams(window.location.search)
    const jType = urlSearchParams.get('jewelry_type')
    const jewelryType = jType !== null ? `?jewelry_type=${jType}` : ''
    history.push(`${jewelryType}#${state.product?.title}#${size.weight}`)
  }

  const closeErrorModal = (): void => {
    setError(null)
    setErrorModalOpen(false)
  }

  const closeConfirmationModal = (): void => {
    setConfirmationModalOpen(false)
  }

  const closeDASOrderSuccessfullModal = (): void => {
    setDasOrderSuccessfullModalOpen(false)
  }

  const handleAddToCart = async (gems: { [key: string]: any }[]): Promise<void> => {
    const result = await storeFunctions.addToCart(gems)
    if (!result.success) {
      setError({
        title: result.error?.errorTitle || '',
        message: result.error?.errorBody || ''
      })
      setErrorModalOpen(true)
      return
    }
    window.location.href = '/cart'
  }

  const handleAddToDAS = useCallback(async (): Promise<void> => {
    const result = await storeFunctions.addToDAS({
      diamonds: selectedGems,
      type: selectedGemsOrderType,
      shop: shopName || '',
      searchType: "diamond_search"
    })
    if (!result.success) {
      setConfirmationModalOpen(false)
      setError({
        title: result.error?.errorTitle || '',
        message: result.error?.errorBody || ''
      })
      setErrorModalOpen(true)
      return
    }
    setConfirmationModalOpen(false)
    setDasOrderSuccessfullModalOpen(true)
  }, [selectedGems, selectedGemsOrderType, shopName])

  const handleDASOrdering = (diamondsData: { [key: string]: any }, orderType: string): void => {
    const gems = Array.isArray(diamondsData) ? diamondsData : [diamondsData]
    setSelectedGems(gems)
    setSelectedGemsOrderType(orderType)
    setConfirmationModalOpen(true)
  }

  const searchParams = new URLSearchParams(window.location.search)
  const jType = searchParams.get('jewelry_type')
  const mode =
    jType == 'ring'
      ? JewelryType.Ring
      : jType == 'pendant'
      ? JewelryType.Pendant
      : jType == 'bracelet'
      ? JewelryType.Bracelet
      : jType == 'earring'
      ? JewelryType.Earring
      : ''

  const handleAddJewelryItems = (items: Array<any>): void => {
    if (
      (items.length > 1 && bespokeType === 'ai_solitaire') ||
      (items.length > 2 && bespokeType === 'ai_pair')
    ) {
      setError({
        title: t(
          bespokeType === 'ai_solitaire' ? 'bespoke.onlyOneDiamond' : 'bespoke.onlyOnePairOfDiamonds'
        ),
        message: ''
      })
      setErrorModalOpen(true)
      return
    }
    const jewelryType = jType !== null ? `?jewelry_type=${jType}` : ''
    history.push(
      `/apps/ub/diamond_collection/${items
        .map((item: { [key: string]: any }) => item.sku)
        .join('::')}${jewelryType}`
    )
  }

  const wishlistProps = useWishlist('diamond')

  const bespokePageProps: ProductCollectionPageProps = {
    locale: getSearchSdkLocale(),
    shareProps: { hidden: true },
    merchant: { company_name: shopName || '', first_name: '' },
    productCollection: state.product,
    collection: state.collection,
    size: state.size,
    enabledSourceTypes: enabledSourceTypes,
    onProductCollectionChange: onProductCollectionChange,
    onSizeChange: onSizeChange,
    mode: bespokeType,
    api: {
      getDiamonds: async (params: {
        [key: string]: any
      }): Promise<{ [key: string]: any }[] | null> => await getDiamondsByMerchantTemplates(params)
    },
    role: role,
    isActivated: true,
    isOnboarded: true,
    viewerUrl: process.env.VIEWER_URL,
    v360PlayerUrl: `${location.origin}/apps/ub/360player`,
    weights: state.weights,
    onAddJewelryItems: handleAddJewelryItems,
    getIsJewelryInStock: async (items: Array<any>): Promise<boolean> => {
      if (items.length === 0) {
        return false
      }
      if (
        (items.length > 1 && bespokeType === 'ai_solitaire') ||
        (items.length > 2 && bespokeType === 'ai_pair')
      ) {
        return true
      }
      try {
        const mode = bespokeType === 'ai_solitaire' ? 'single' : 'pair'
        const productIds = await storeFunctions.tagProductsForSKU(items, mode)
        if (productIds) {
          return true
        }
        return false
      } catch (e) {
        return false
      }
    },
    ...(config.advancedSearchCartEnabled ? { onAddCartItems: handleAddToCart } : {}),
    ...(isMemoMode
      ? {
          onAddInStoreItems: (items: Array<any>): void => handleDASOrdering(items, 'i'),
          onAddMemoItems: (items: Array<any>): void => handleDASOrdering(items, 'm')
        }
      : {}),
    currencyProps: {
      // Regular customers get the price already converted to their currency
      // (retail_price x Shopify rate above), so single mode shows it as-is —
      // matching MainDiamondSearch. Merchant/memo keep the double-mode view.
      mode: role === Roles.Merchant || isMemoMode ? 'double' : 'single',
      currencies: currencyInfo.currencies,
      currencyCode: currencyInfo.currency,
      // Second (customer-currency) line = price x this rate. Use the Shopify
      // presentment rate so it matches checkout/other surfaces (not DAS's rate).
      // In single mode prices are already converted, so rate is 1 (no re-multiply).
      currencyRate:
        role === Roles.Merchant || isMemoMode
          ? shopCurrency && currencyInfo.currency !== shopCurrency
            ? window.Shopify?.currency?.rate || 1
            : 1
          : 1
    },
    labels: typeLabels,
    ringSizeProps: {
      sizeRegion: config.ringSizeRegion
    }
  }

  if (role === Roles.Merchant || isMemoMode) {
    bespokePageProps.wishlistProps = wishlistProps
  }

  return (
    <>
      <ErrorModal
        open={errorModalOpen}
        title={error?.title}
        message={error?.message}
        onClose={closeErrorModal}
      />
      <ConfirmationModal
        open={confirmationModalOpen}
        gems={selectedGems}
        orderType={selectedGemsOrderType}
        config={config}
        currencyInfo={currencyInfo}
        onConfirm={handleAddToDAS}
        onClose={closeConfirmationModal}
      />
      <DASOrderSuccessfullModal
        open={dasOrderSuccessfullModalOpen}
        gems={selectedGems}
        orderType={selectedGemsOrderType}
        config={config}
        currencyInfo={currencyInfo}
        onClose={closeDASOrderSuccessfullModal}
      />
      {loaded ? (
        <div className="unbridaled-bespoke-diamonds">
          {showJourneyBar ? <JourneyBar activeStep={1} mode={mode} /> : null}
          <ProductCollectionPage
            {...bespokePageProps}
          />
        </div>
      ) : (
        <Preloader preloaderClass="unbridaled-full-page-preloader" />
      )}
    </>
  )
}

export default BespokePage
